import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import {
  MatLegacyDialogRef as MatDialogRef,
  MatLegacyDialog as MatDialog,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Params } from '@angular/router';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, takeUntil, catchError } from 'rxjs/operators';

// INTERFACES
import {
  LocalStorageKeys,
  CustomFieldApiService,
  OptionInterface,
  IPaginatorEvent,
  IPagination,
  PaginationConfig,
  SnackBarConfig,
} from '../../../shared';

import { DialogConfig } from './../../../shared/configs/dialog.config';
import { UiConfirmationDialogComponent } from './../../../ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { ICustomField } from './../../interfaces/custom-field.interface';

@Component({
  selector: 'lum-custom-field',
  templateUrl: './custom-field.component.html',
  styleUrls: ['./custom-field.component.scss'],
})
export class CustomFieldComponent implements OnInit, OnDestroy {
  data :ICustomField[] = [];

  loading :boolean = false;

  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    path: '',
    per_page: PaginationConfig.perPage,
    to: null,
    total: 0,
  };

  queryParams :{ [param :string] :string | string[] };
  searchQuery :string = '';

  private onDestroy$ = new Subject();

  constructor(
    private customFieldApiService :CustomFieldApiService,
    private route :ActivatedRoute,
    private cd :ChangeDetectorRef,
    private snackBar :MatSnackBar,
    private dialog :MatDialog,
  ) {}

  ngOnInit() :void {
    this.initQueryParams();
    this.loadData();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onSearchEvent(searchQuery :string) :void {
    this.searchQuery = searchQuery;
    this.queryParams['page'] = PaginationConfig.page.toString();
    this.loadData();
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.queryParams = {
      ...this.queryParams,
      limit: pagination.pageSize.toString(),
      page: (pagination.pageIndex + 1).toString(),
    };
    this.loadData();
  }

  onDeleteEvent(id :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Confirm Custom Field Deletion?',
          message:
            'Please remember, removing this custom field means the linked data will no longer be accessible.',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loading = true;

          this.customFieldApiService
            .delete(id)
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loading = false;
                this.snackBar.open(response.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.loadData();
              },
              error: (response) => {
                this.loading = false;
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });
                this.cd.detectChanges();
              },
            });
        }
      });
  }

  private resetFilters() :void {
    this.initQueryParams();
    this.loadData();
  }

  private initQueryParams() :void {
    this.queryParams = {
      limit: PaginationConfig.perPage.toString(),
      page: PaginationConfig.page.toString(),
      sortDirection: 'desc',
      sortField: 'created_at',
    };
  }

  private loadData() :void {
    this.loading = true;
    this.customFieldApiService
      .get({
        ...this.queryParams,
        searchQuery: this.searchQuery,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loading = false;
          this.data = response?.data;
          this.pagination = response?.meta;
          this.cd.detectChanges();
        },
        error: (response) => {
          this.loading = false;
          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.cd.detectChanges();
        },
      });
  }
}
